import { useQuery } from "@tanstack/react-query";
import jwt_decode from "jwt-decode";
import { api } from "../lib/api";
import { getSessionToken } from "../lib/sessionToken";

export interface BannerNotification {
  id: number | string;
  title: string | null;
  notification_message: string;
  urgency: string | null;
  updated_at: string;
}

// Authenticated banners are targeted by user and organization, and logout keeps
// the query cache, so the key carries the signed-in user. Otherwise the next
// account signed in on the same page would be handed the previous one's still
// fresh banners.
export const bannerNotificationKeys = {
  all: ["bannerNotifications"] as const,
  scope: (userId: string | null) => ["bannerNotifications", userId ?? "public"] as const,
};

const getSignedInUserId = (): string | null => {
  const token = getSessionToken();
  if (!token) {
    return null;
  }
  try {
    return jwt_decode<{ user?: { id?: string } }>(token)?.user?.id ?? null;
  } catch {
    return null;
  }
};

export const POLL_INTERVAL_MS = 2 * 60 * 1000;

/**
 * Polls for admin-authored banners. Anonymous visitors get the public feed,
 * which only carries broadcast-to-everyone rows.
 *
 * Every open tab mounts this, including background ones, so polling pauses
 * while the tab is hidden and catches up when it becomes visible again. The
 * staleTime matches the interval so a quick tab switch does not refetch.
 */
export function useBannerNotifications(isAuthenticated: boolean | null) {
  const userId = isAuthenticated ? getSignedInUserId() : null;

  return useQuery({
    queryKey: bannerNotificationKeys.scope(userId),
    // An authenticated session whose token names no user has nothing safe to
    // key on; its request would be rejected anyway.
    enabled: isAuthenticated === false || (isAuthenticated === true && userId !== null),
    queryFn: async (): Promise<BannerNotification[]> => {
      const endpoint = isAuthenticated ? "/notifications" : "/notifications/public";
      const data = await api.get<unknown>(endpoint, { auth: Boolean(isAuthenticated) });
      // Both routes answer with an array of rows or a 204; the shared client hands
      // a 204 back as an empty string because there is no JSON body to parse.
      return Array.isArray(data) ? (data as BannerNotification[]) : [];
    },
    staleTime: POLL_INTERVAL_MS,
    refetchInterval: POLL_INTERVAL_MS,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    // A failed poll keeps the last banners on screen, and the next interval
    // tries again; retrying a 403 from an expired session only adds load.
    retry: false,
  });
}

export default useBannerNotifications;
